import { Component, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { BooksService } from '../../core/services/data/books.service';

import { Book } from '../../classes/book';
import { SettingsService } from '../../core/services/data/settings.service';

import { RaceBookAPIResponse, ScrapeAPIResponse } from '../../types/responses';

@Component({
	selector: 'app-new-book',
	templateUrl: './new-book.component.html',
	styleUrls: ['./new-book.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})

export class NewBookComponent {
	@Output() bookCreated = new EventEmitter<any>();

	public ASIN: string;

	public book = new Book();

	public bookImageUploadUrl;

	public error = {
		title: '',
		text: '',
		occured: false,
	};

	public form: UntypedFormGroup;

	public scrapeInProgress = false;

	public url: string;

	constructor(
		private bookService: BooksService,
		private settingsService: SettingsService,
	) {
		this.form = new UntypedFormGroup({
			amazonLink: new UntypedFormControl(''),
			title: new UntypedFormControl(''),
			author: new UntypedFormControl(''),
			description: new UntypedFormControl(''),
		});

		this.form.valueChanges.subscribe(() => {
			this.error.occured = false;
		});
	}

	get buttonText() {
		const url = this.form.controls.amazonLink.value;
		if (url.length === 0 && this.error.occured) {
			return 'Proceed without link';
		}

		return this.scrapeInProgress ? 'Loading...' : 'Find Book';
	}

	async scrapeLink() {
		let url = this.form.controls.amazonLink.value;

		if (url.toLowerCase() === 'TBD') {
			url = 'https://www.amazon.com/dp/null';
		}

		const { valid, message } = this.validateAmazonUrl(url);

		if (!valid) {
			if (message !== 'Missing Amazon URL' || this.error.occured) {
				url = 'https://www.amazon.com/dp/null';
			} else {
				return this.handleInvalidURL();
			}
		}

		if (url === 'https://www.amazon.com/dp/null') {
			this.book.title = 'TBD';
			this.book.author = 'TBD';
			this.book.amazonLink = 'https://www.amazon.com/dp/null';
			this.url = url;
			this.form.controls.amazonLink.patchValue(this.url);
			return this.handleSuccess();
		}

		this.scrapeInProgress = true;
		this.ASIN = Book.parseAmazonId(url);
		if (this.ASIN === null) {
			this.scrapeInProgress = false;
			return this.handleInvalidURL();
		}
		this.url = `https://www.amazon.com/dp/${this.ASIN}`;
		this.form.controls.amazonLink.patchValue(this.url);

		const useV2Scraper = this.settingsService.getSetting('scrape_v2') === 'true';

		if (useV2Scraper) {
			return this.bookService.getBookData(this.ASIN).subscribe((result: RaceBookAPIResponse) => this.handleScrapeResultV2(result));
		}

		return this.bookService.scrape(url).subscribe((result: ScrapeAPIResponse) => this.handleScrapeResponse(result));
	}

	validateAmazonUrl(url) {
		if (!url) {
			return { valid: false, message: 'Missing Amazon URL' };
		}

		if (url === 'https://www.amazon.com/dp/null') {
			return { valid: true, message: '' };
		}
		let _url;
		// Catch invalid URLs.
		// eslint-disable-next-line no-restricted-syntax
		try {
			_url = new URL(url);
		} catch (err) {
			return { valid: false, message: 'Invalid Amazon URL 2' };
		}

		const regex = /amazon\./;
		if (!regex.test(_url.hostname) || /[0-9A-Z]{10}/.test(_url.pathname) === false) {
			return { valid: false, message: 'Invalid Amazon URL 1' };
		}

		return { valid: true, message: '' };
	}

	handleInvalidURL() {
		this.error.title = 'Invalid Amazon Url';
		this.error.text = 'Hmm, that doesn\'t look like a valid Amazon link.'
	  + ' If your book is not yet available on Amazon, just leave the Amazon URL field'
	  + ' blank and we will automatically put in a null placeholder URL for you. You can enter TBD in the title field '
	  + 'if your book doesn\'t have a title yet.';
		this.error.occured = true;
	}

	getAmazonError(data) {
		return data?.message?.Error?.[0]?.Code?.[0];
	}

	updateCover(cover) {
		this.book.amazonImageUrl = cover;
	}

	handleScrapeResponse(data: any) {
		this.scrapeInProgress = false;

		if (!data || !data?.success) {
			const amazonError = this.getAmazonError(data);

			if (amazonError === 'InvalidClientTokenId') {
				return this.handleInvalidTokens();
			}

			return this.handleBookNotFound();
		}

		const { library, scrapeResult } = data;
		const bookImage = library.cover || scrapeResult.cover; // Temporarily handle this from either source. Ideally library has everything.
		this.book.ASIN = this.ASIN;
		this.book.amazonLink = this.url;
		this.book.author = library.author;
		this.form.controls.author.patchValue(this.book.author);
		this.book.title = library.title;
		this.form.controls.title.patchValue(library.title);
		this.book.amazonImageUrl = bookImage || this.bookImageUploadUrl;
		this.book.amazonBookDescription = Book.parseAmazonBookDescription(data.scrapeResult.description);

		return this.handleSuccess();
	}

	handleScrapeResultV2({ success, title, author, cover, libraryId }: RaceBookAPIResponse) {
		this.scrapeInProgress = false;
		if (!success) {
			return this.handleBookNotFound();
		}
		const bookImage = cover; // Temporarily handle this from either source. Ideally library has everything.
		this.book.ASIN = this.ASIN;
		this.book.amazonLink = this.url;
		this.book.author = author;
		this.form.controls.author.patchValue(this.book.author);
		this.book.title = title;
		this.form.controls.title.patchValue(title);
		this.book.amazonImageUrl = bookImage || this.bookImageUploadUrl;

		// this.book = { ...this.book };
		return this.handleSuccess();
	}

	save() {
		return new Promise((resolve, reject) => this.bookService.save(this.book).subscribe((result: any) => resolve(result.book), reject));
	}

	async handleSuccess() {
		const book = await this.save();
		this.bookCreated.emit(book);
	}

	continue() {
		if (this.form.controls.amazonLink.value) {
			return this.scrapeLink();
		}
		this.book.title = this.form.controls.title.value;
		this.book.author = this.form.controls.author.value;
		this.book.description = this.form.controls.description.value;

		return this.handleSuccess();
	}

	handleInvalidTokens() {
		this.error.title = 'Amazon Error';
		this.error.text = 'We\'re having trouble connecting to Amazon.  Please try again later.';
		this.error.occured = true;
	}

	handleBookNotFound() {
		this.error.title = 'Well, This is awkward…';
		this.error.text = 'It seems Amazon didn’t share your book information with us. 🤔\n'
			+ '\n'
			+ 'No worries! Just fill out the form below to schedule your Promo, and we’ll take it from there. 📚';
		this.error.occured = true;
	}
}
